// WS-75 perf: fewer distinct render pipelines, compiled side by side (engine-wide, resort-agnostic).
//
// Every WebGPU pipeline is a driver compile (D3D on Windows: 100-400 ms each for a lit material), so the start costs
// what the number of *distinct* pipelines costs, and how many of them compile at once.
//
// 1. sharedInstancePrograms(): three r186 gives an InstancedMesh of <= 1024 instances its matrices as a uniform array
//    named after a per-mesh node id (`NodeBuffer_<id>`, sized to the count), so every such mesh had a vertex program and
//    a pipeline of its own, even with the same material, and a new one whenever its instance buffer was rebuilt (the
//    first frame after the warm start rebuilt ~15). Here they read the matrices as instanced vertex attributes, as three
//    already does above 1024 instances (the forest): the program is the same for every mesh of a material, so they share
//    one pipeline. (main.js has since set the limit itself to 0: every InstancedMesh reads its matrices as a vertex
//    buffer, 4 inputs; the herds and people, skinned in world space, read none in their colour pass: gpu/skin.js.)
// 2. parallelPipelines(): three r187's compileAsync builds an object's nodes and starts its pipeline, up to 16 in flight.
//    The pipelines it starts are kept aside instead (its own list stays empty, so it never waits) and pending() returns
//    them all: render.js prewarm() waits for them together and the browser compiles them in parallel, with no cap.
//    Draws and the renderer's own sync path (promises = null) are untouched. VIS-267: at most 4 in flight on the WebGL2
//    backend only (batchPipelines / warmBatchFor below).
import * as THREE from 'three/webgpu';
import { usable, pipelines, renderContexts, nodes } from './internals.js'; // VIS-194: three's private parts, checked at start-up

export function sharedInstancePrograms() {
  const NB = THREE.NodeBuilder.prototype;
  if (NB._ws75inst || !usable('NodeBuilder.getUniformBufferLimit')) return;
  NB._ws75inst = true;
  const limit = NB.getUniformBufferLimit;
  NB.getUniformBufferLimit = function () {
    const o = this.object;
    if (o?.isInstancedMesh && o.userData?.uniformInstances !== true && Object.keys(o.geometry?.attributes || {}).length <= 6) return 0; // 0 = "too big": the attribute path
    return limit.call(this);
  };
}

const _pending = new Set();
export function parallelPipelines(renderer) {
  const P = pipelines(renderer);
  if (!P || P._ws75par) return;
  P._ws75par = true;
  const get = P.getForRender;
  P.getForRender = function (renderObject, promises = null) {
    if (promises === null) return get.call(this, renderObject, null);
    const mine = [], res = get.call(this, renderObject, mine);
    for (const p of mine) { _pending.add(p); _started++; p.then(() => { _pending.delete(p); _done++; for (const f of _heard) f(); }); }
    if (_batch > 0) promises.push(...mine); // the WebGL2 backend: compileAsync keeps WEBGL_WARM_BATCH in flight (scripts/vendor.mjs patchCompileLane)
    return res;
  };
}
/** Every pipeline started by a compileAsync and not compiled yet (they resolve, never reject). */
export const pending = () => Promise.all([..._pending]);
/** Runs draw (its renderer.render calls); false when three skipped something in it, a pipeline not ready (compiling, or
 *  failed: gpu/programs.js builds it again at a next draw). For a map drawn once until something moves, which would keep
 *  the hole for good: lazy/lightcast.js. shadows.js's keeper watches the same way; rest.js's digest too (not drawn: no sleep). */
export function drewAll(renderer, draw) {
  const P = pipelines(renderer), ready = P?.isReady;
  let all = true;
  if (ready) P.isReady = function (ro) { const ok = ready.call(this, ro); all &&= ok; return ok; };
  try { draw(); } finally { if (ready) P.isReady = ready; }
  return all;
}
/** clouds-light: a dry draw. draw()'s pipelines start compiling instead (three skips a draw whose pipeline is not
 *  ready), in the real draw's own context (target, call depth, a QuadMesh's vertex stage), as render.js warmPost does;
 *  resolves once they are compiled (with what else is pending then). lazy/clouds.js warms its noise and pass so. */
export function dryDraw(renderer, draw) {
  const P = pipelines(renderer), mine = [], own = P && Object.hasOwn(P, 'updateForRender') ? P.updateForRender : null;
  if (P) P.updateForRender = function (ro) { return this.getForRender(ro, mine); };
  try { draw(); } finally { if (own) P.updateForRender = own; else if (P) delete P.updateForRender; }
  return Promise.all([...mine, pending()]);
}
// VIS-267: how many pipelines compileAsync started and how many have compiled (monotonic), and a call on each one done
// (render.js prewarm: the loader's bar moves with each, and the start's watchdog hears it).
let _started = 0, _done = 0;
const _heard = new Set();
export const compiles = () => ({ started: _started, done: _done, inFlight: _pending.size });
export function onCompiled(fn) { _heard.add(fn); return () => _heard.delete(fn); }
// VIS-267 W6: the warm start at most n in flight, on the WebGL2 backend only. The limit bites only where
// KHR_parallel_shader_compile exists (three polls each link's status), and keeps the driver's links in flight to n (a
// phone: 61-63 programs). Without it three links each program at once on the main thread: no promise, nothing to limit,
// and the bar moves per object. The promises go to compileAsync, whose limit there is n (scripts/vendor.mjs
// patchCompileLane has the same number). 0 = none handed over, no limit.
// On WebGPU the browser compiles off the main thread (Chrome: Dawn's worker threads; Safari: one work queue in its GPU
// process, one pipeline at a time on every shipped build: 321042@main's parallel compile is on WebKit's main only, so
// there workers compile ahead of it, lazy/compilehelpers.js), and
// compileAsync builds the next object's nodes while they compile. A cap there made the two take turns (node builds,
// then the slowest compile, then a round trip back):
// "Preparing the drawing" took 22 s on an iPhone 13 Pro (VIS-267, Sentry). So WebGPU starts every pipeline at once, past
// three's own 16.
export const WEBGL_WARM_BATCH = 4;
export const warmBatchFor = (backend) => (backend?.isWebGLBackend ? WEBGL_WARM_BATCH : 0);
/** Does this browser compile one pipeline at a time? Every WebKit before Safari 27.3 (its notes: "asynchronous pipeline
 *  creation could stall rendering", WebKit 321042@main; the owner's iPhone on 27.3, 2026-11-04: Ready 33 s without the
 *  helpers, 36 s with them); on iOS, where Chrome and the in-app browsers have no `Version/`, any WebKit counts. */
export function serialCompiles(ua) {
  if (!/AppleWebKit/.test(ua) || /Chrome|Chromium/.test(ua)) return false;
  const v = /Version\/(\d+)(?:\.(\d+))?/.exec(ua);
  return !v || !(+v[1] > 27 || (+v[1] === 27 && +(v[2] ?? 0) >= 3));
}
let _batch = 0;
export function batchPipelines(n) { _batch = Math.max(0, n | 0); }

// 3. compileAs(): three keys a render context (and so every render object, node build and program) by its target's
//    format AND by the renderer's call depth. A scene pass inside the post chain draws at depth 2 (canvas 0 -> finish
//    texture 1 -> scene 2), but compileAsync asks for depth 0: the warm start built every object's nodes for a context
//    the draw never uses, the first frame built them all again (JS) and compiled again each program whose WGSL came out
//    in another order (the terrain, the most shared material, the glass). compileAs(renderer, target, fn) runs fn (a
//    compileAsync call) with the depth `target` was last drawn at (`depth` until it has been drawn).
//    VIS-267 step 4: trackDepths() first, then a draw of the chain (render.js warmPost, over an empty scene), so the
//    warm start knows the depth: on the phone set (oneOutput) the scene pass is not at 2, and the first picture built
//    the scene's ~45 programs again (iPhone 13 Pro: the seconds of "0 frames at the start pose").
export function trackDepths(renderer) {
  const rc = renderContexts(renderer);
  if (!rc || rc._ws75depth) return rc;
  rc._ws75depth = new WeakMap(); // remember the depth each target is drawn at (one Map write per render call)
  const get0 = rc.get;
  rc.get = function (rt = null, mrt = null, d = 0, camera = null) { if (rt) rc._ws75depth.set(rt, d); return get0.call(this, rt, mrt, d, camera); };
  rc._ws75get0 = get0;
  return rc;
}
export function compileAs(renderer, target, fn, depth = 2) {
  const rc = trackDepths(renderer);
  if (!rc) return fn();
  const d = rc._ws75depth.get(target) ?? depth, get = rc.get;
  rc.get = function (rt = null, mrt = null, dd = 0, camera = null) { return rc._ws75get0.call(this, rt, mrt, rt === target ? d : dd, camera); };
  try { return fn(); } finally { rc.get = get; } // compileAsync reads its context before its first await
}

// 4. sharedValueBuilds(): three keys a node build (the TSL graph -> WGSL, ~90 ms on the main thread for a lit material
//    at 4x CPU) by every material property, a number reduced to zero / not zero. For the numbers in VALUE_ONLY the
//    program is the same either way (three reads them from the material as uniforms), so a material with metalness 0
//    and one with 0.1 built the same program twice. Here a zero counts as "not zero" in that key (the render pipeline
//    and the uniforms are keyed and bound as before). A property with an accessor of its own (materials.js
//    bakedInterior's emissiveIntensity) is left alone. Measure: docs/qa/ws75/builds.mjs (repeats, and why keys differed).
const VALUE_ONLY = ['metalness', 'roughness', 'emissiveIntensity', 'envMapIntensity'];
export function sharedValueBuilds(renderer) {
  const N = nodes(renderer);
  if (!N || N._ws75val) return;
  N._ws75val = true;
  const K = Symbol('ws75key'), K0 = Symbol('ws75key0');
  N.getForRenderCacheKey = function (ro) { // also used to drop the build on dispose: the key is kept on the render object
    if (ro[K0] === ro.initialCacheKey) return ro[K];
    const m = ro.material, saved = [];
    for (const p of VALUE_ONLY) {
      const d = Object.getOwnPropertyDescriptor(m, p);
      if (d && d.writable && d.value === 0) { saved.push(p); m[p] = 1; }
    }
    let k = ro.initialCacheKey;
    if (saved.length) try { k = ro.getCacheKey(); } finally { for (const p of saved) m[p] = 0; }
    ro[K0] = ro.initialCacheKey; ro[K] = k;
    return k;
  };
}
